import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { campaignTypeDefinition, campaignTypeFromStored, type CampaignType } from "../../src/domain/campaignTypes";
import {
  campaignRecommendationsFromStored,
  campaignTargetingFromStored,
  recommendationModeLabel,
  type CampaignRecommendationMode,
  type CampaignTargeting,
} from "../../src/domain/campaignConfig";
import { apiFetch } from "../api";

type SavedCampaign = {
  id: string;
  name: string;
  status: "draft" | "active";
  priority: number;
  campaignType?: CampaignType;
  trigger?: "pre_checkout" | "after_add" | "product_page";
  targeting?: CampaignTargeting;
  recommendations?: { mode?: CampaignRecommendationMode };
  updatedAt: string;
  rule?: { condition: string; value: string };
};

type StatusFilter = "all" | "active" | "draft";

export function Campaigns() {
  const [campaigns, setCampaigns] = useState<SavedCampaign[]>([]);
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");

  function loadCampaigns() {
    apiFetch("/api/campaigns")
      .then((response) => (response.ok ? response.json() : undefined))
      .then((payload: { campaigns?: SavedCampaign[] } | undefined) => setCampaigns(payload?.campaigns ?? []))
      .catch(() => setCampaigns([]));
  }

  useEffect(loadCampaigns, []);

  const visibleCampaigns = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      if (filter !== "all" && campaign.status !== filter) return false;
      return !normalizedQuery || campaign.name.toLowerCase().includes(normalizedQuery);
    });
  }, [campaigns, filter, query]);

  async function deleteCampaign(id: string) {
    const campaign = campaigns.find((item) => item.id === id);
    if (!window.confirm(`Delete ${campaign?.name ?? "this campaign"}?`)) return;
    const response = await apiFetch(`/api/campaigns/${encodeURIComponent(id)}`, { method: "DELETE" });
    if (response.ok) {
      const payload = (await response.json()) as { campaigns?: SavedCampaign[] };
      setCampaigns(payload.campaigns ?? []);
      setMessage("Campaign deleted.");
    } else setMessage("Campaign could not be deleted.");
  }

  function countFor(status: StatusFilter) {
    return status === "all" ? campaigns.length : campaigns.filter((campaign) => campaign.status === status).length;
  }

  return (
    <s-page heading="Campaigns">
      <header className="page-header">
        <div><s-paragraph>Create and manage offers across your storefront.</s-paragraph></div>
        <Link className="button primary" to="/campaigns/new">Create campaign</Link>
      </header>
      {message ? <p className={message.includes("could not") ? "inline-error" : "inline-success"}>{message}</p> : null}
      <s-section heading="All campaigns">
        <div className="campaign-toolbar">
          <div className="campaign-tabs" role="tablist" aria-label="Campaign status">
            {(["all", "active", "draft"] as StatusFilter[]).map((status) => (
              <button className={filter === status ? "active" : ""} key={status} type="button" onClick={() => setFilter(status)}>
                {status[0].toUpperCase() + status.slice(1)} <span>{countFor(status)}</span>
              </button>
            ))}
          </div>
          <label className="campaign-search"><span className="sr-only">Search campaigns</span><input type="search" placeholder="Search campaigns" value={query} onChange={(event) => setQuery(event.currentTarget.value)} /></label>
        </div>
        <div className="table-scroll">
          <table className="campaign-table">
            <thead><tr><th>Campaign</th><th>Status</th><th>Placement</th><th>Show for</th><th>Recommendation</th><th>Priority</th><th>Updated</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {visibleCampaigns.map((campaign) => {
                const definition = campaignTypeDefinition(campaignTypeFromStored(campaign));
                const targeting = campaignTargetingFromStored(campaign);
                const recommendation = campaignRecommendationsFromStored(campaign);
                const targetingLabel = targeting.mode === "all"
                  ? "All products"
                  : targeting.mode === "specific_products"
                    ? `${targeting.products.length} product${targeting.products.length === 1 ? "" : "s"}`
                    : `${targeting.collections.length} collection${targeting.collections.length === 1 ? "" : "s"}`;
                return (
                  <tr key={campaign.id}>
                    <td><Link className="campaign-name" to={`/campaigns/${campaign.id}`}>{campaign.name}</Link><small>{definition.title}</small></td>
                    <td><span className={`status-pill ${campaign.status}`}>{campaign.status}</span></td>
                    <td>{definition.placementLabel}</td>
                    <td>{targetingLabel}</td>
                    <td>{recommendationModeLabel(recommendation.mode)}</td>
                    <td>{campaign.priority}</td>
                    <td>{campaign.updatedAt ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(campaign.updatedAt)) : "—"}</td>
                    <td><div className="row-actions"><Link className="plain-link" to={`/campaigns/${campaign.id}`}>Edit</Link><button className="plain-button" type="button" onClick={() => deleteCampaign(campaign.id)}>Delete</button></div></td>
                  </tr>
                );
              })}
              {!visibleCampaigns.length ? <tr><td colSpan={8}><div className="table-empty"><strong>{campaigns.length ? "No matching campaigns" : "No campaigns yet"}</strong><small>{campaigns.length ? "Try another search or status filter." : "Choose a campaign type to create your first offer."}</small>{!campaigns.length ? <Link className="polaris-button" to="/campaigns/new">Create campaign</Link> : null}</div></td></tr> : null}
            </tbody>
          </table>
        </div>
      </s-section>
    </s-page>
  );
}
